/* eslint-disable max-len */
import { Checkbox, Form, Modal, Row, Table } from 'antd';
import React, { useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import AgendaAmlz from 'components/pages/spravaEmitenta/agendaAmlz/formular/AgendaAmlzFormular';

import {
    IDokOsoba,
    IDokOsoby,
    dokumentOsobyApi,
    dokumentOsobyPodpis,
    dokumentOsobyUpdateApi
} from 'services/dokumenty/smlouva';
import { amlzC2osobyDetailGet } from 'services/spravaEmitenta/amlzData';
import { ISubjPravo } from 'services/spravaEmitenta/zadostPravo';

import { EStavPodpisu } from 'enums/dokumentyEnums';

interface IOsoby {
    nextStep: () => void;
    prevStep: () => void;
    zadostID: number;
    isDodatek?: boolean;
}

const Osoby: React.FC<IOsoby> = ({ nextStep, prevStep, zadostID, isDodatek }) => {
    const { confirm } = Modal;
    const [form] = Form.useForm();
    const [osoby, setOsoby] = useState<IDokOsoby>();
    const [personData, setPersonData] = useState<ISubjPravo | undefined>();
    const [loading, setLoading] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const hasOsobySelected = osoby?.osoby.filter(e => e.podepisuje).length;
    const hasOsobySigned = osoby?.osoby.filter(
        e => e.stavpodpisu === EStavPodpisu.ODESLANO || e.stavpodpisu === EStavPodpisu.PODEPSANO
    ).length;

    useEffect(() => {
        setLoading(true);
        dokumentOsobyApi(zadostID)
            .then(resp => setOsoby(resp))
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch]);

    const osobyCdcpCols = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno'
        },
        {
            title: <TranslateText>FUNKCE</TranslateText>,
            dataIndex: 'funkce',
            key: 'funkce'
        },
        {
            title: <TranslateText>ZPUSOB_ZSTUPENI</TranslateText>,
            dataIndex: 'typzast',
            key: 'typzast'
        }
    ];
    const osobyCols = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (jmeno: string, row: IDokOsoba) => `${jmeno} ${row.prijmeni}`
        },
        {
            title: <TranslateText>EMAIL</TranslateText>,
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: <TranslateText>TELEFON</TranslateText>,
            dataIndex: 'telefon',
            key: 'telefon'
        },
        {
            title: <TranslateText>OSOBA_JE</TranslateText>,
            dataIndex: 'emitentazas',
            key: 'emitentazas',
            render: (emitentazas: string) => <TranslateText>{'ISIN_ZASTUPUJE_OSOBA_' + emitentazas}</TranslateText>
        },
        {
            title: <TranslateText>PODPIS</TranslateText>,
            dataIndex: 'emitentazas',
            key: 'emitentazas',
            render: (_a: string, row: IDokOsoba) => (
                <Checkbox
                    checked={row.podepisuje}
                    onClick={(ev: any) => {
                        if (hasOsobySigned) {
                            confirm({
                                title: <TranslateText>DOKUMENTY_OSOBY_ZMENIT</TranslateText>,
                                cancelText: <TranslateText>NE</TranslateText>,
                                okText: <TranslateText>ANO</TranslateText>,
                                onOk() {
                                    setLoading(true);
                                    dokumentOsobyPodpis(zadostID, row.id, !ev.target.checked)
                                        .then(() => {
                                            setRefetch(pv => !pv);
                                        })
                                        .finally(() => setLoading(false));
                                }
                            });
                        } else {
                            setLoading(true);
                            dokumentOsobyPodpis(zadostID, row.id, ev.target.checked)
                                .then(() => {
                                    setRefetch(pv => !pv);
                                })
                                .finally(() => setLoading(false));
                        }
                    }}
                />
            )
        },
        {
            render: (_text: string, row: IDokOsoba, index: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'right'
                    }}
                >
                    {
                        <AnchorComponent
                            id={`OsobyZobrazitBtn${index}`}
                            key={`OsobyZobrazitBtn${index}`}
                            onClick={() => {
                                setLoading(true);
                                amlzC2osobyDetailGet(row.zadostoid, row.c2dokosobaoid)
                                    .then(resp => setPersonData(resp))
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <TranslateText>ZOBRAZIT</TranslateText>
                        </AnchorComponent>
                    }
                </div>
            )
        }
    ];

    return !personData ? (
        <LoadingWrapper loading={loading}>
            <FormWrapper
                outForm={form}
                onFinish={() => {
                    nextStep();
                }}
                name="Osoby"
            >
                <Title level={5} text="DOKUMENTY_OSOBY_CDCP" dividerTitle={true} />
                <Table
                    rowKey={(_, index) => index || 0}
                    locale={{
                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                    }}
                    style={{
                        margin: '30px 0px'
                    }}
                    columns={osobyCdcpCols}
                    dataSource={osoby?.cdcpzast}
                    pagination={false}
                    rowClassName={(_a, i) => {
                        const classes = [];
                        if (i % 2) classes.push('tableRowGray');
                        return classes.join(' ');
                    }}
                />

                <Row
                    style={{
                        marginTop: 16
                    }}
                />
                <Title level={5} text="ISIN_ZASTUPUJE_NADPIS" dividerTitle={true}>
                    {osoby?.aktualizujosoby && (
                        <ButtonComponent
                            buttontext="DOKUMENTY_OSOBY_NACIST_BTN"
                            onClick={() => {
                                confirm({
                                    title: <TranslateText>DOKUMENTY_OSOBY_NACIST</TranslateText>,
                                    cancelText: <TranslateText>NE</TranslateText>,
                                    okText: <TranslateText>ANO</TranslateText>,
                                    onOk() {
                                        setLoading(true);
                                        dokumentOsobyUpdateApi(zadostID)
                                            .then(() => {
                                                setRefetch(pv => !pv);
                                            })
                                            .finally(() => setLoading(false));
                                    }
                                });
                            }}
                            style={{
                                marginLeft: 10
                            }}
                            id="AmlzNacistBtn"
                        />
                    )}
                </Title>

                <Table
                    rowKey={(_, index) => index || 0}
                    locale={{
                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                    }}
                    style={{
                        margin: '30px 0px'
                    }}
                    columns={osobyCols}
                    dataSource={osoby?.osoby}
                    pagination={false}
                    rowClassName={(_a, i) => {
                        const classes = [];
                        if (i % 2) classes.push('tableRowGray');
                        return classes.join(' ');
                    }}
                />

                <Paragraph
                    style={{
                        display: 'block'
                    }}
                    text={'DOKUMENTY_OSOBY_TEXT'}
                />
                <Row className="button-row">
                    <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="OsobyZpetBtn" type="default" />
                    <ButtonComponent
                        buttontext="POKRACOVAT"
                        id="OsobyPokracovatBtn"
                        disabled={!hasOsobySelected}
                        type="primary"
                        onClick={() => (isDodatek ? form.submit() : nextStep())}
                    />
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    ) : (
        <LoadingWrapper loading={loading}>
            <Title level={5} text="ISIN_ZASTUPUJE_NADPIS" />
            <AgendaAmlz
                mode={'displayOnly'}
                apiList={(() => {}) as any}
                personData={personData}
                setPersonData={setPersonData}
                pdfCheckError={''}
                setPdfCheckError={() => {}}
                setSubmitPersonData={() => {}}
                setshowInstrukceAfterFinish={() => {}}
                idZadosti={undefined}
            />
        </LoadingWrapper>
    );
};

export default Osoby;
